import { useMemo, type ReactElement } from 'react';
import { Box, Group, Stack, Text } from '@mantine/core';
import { Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { AdHocAggregateResult, AdHocTrendResult, AnalyticsField } from '@/shared/analytics/AnalyticsTypes';
import type { AssistChartPlan } from '@/shared/assist/AssistContract';
import { accentRgba, accentTokens } from '@/design/VizAccents';
import type { VizAccent } from '@/design/VizAccents';
import { formatCompact, formatCount, formatMetricValue, formatPercent, metricLabel, humanUser } from '@/shared/analytics/MetricFormatting';

const MAX_DONUT_SLICES = 8;

interface SurfaceAssistChartProps {
    plan: AssistChartPlan;
    aggregate: AdHocAggregateResult | null;
    trend: AdHocTrendResult | null;
    onDrill: (field: AnalyticsField, value: string) => void;
    /** Accent color for the chart; defaults to indigo (the assist's house accent). */
    accent?: VizAccent;
}

function displayLabel(field: AnalyticsField, value: string): string {
    return field === 'user_id' ? humanUser(value) : value;
}

/** Renders a plan's chart (ranked bars, donut, or trend). Bar/slice clicks drill via {@link onDrill}; trend is display-only. */
export default function SurfaceAssistChart({ plan, aggregate, trend, onDrill, accent = 'indigo' }: SurfaceAssistChartProps): ReactElement | null {
    if (plan.viz === 'trend') {
        return trend === null ? null : <TrendChart plan={plan} trend={trend} accent={accent} />;
    }
    if (aggregate === null) return null;
    if (plan.viz === 'donut') {
        return <DonutChart plan={plan} result={aggregate} onDrill={onDrill} accent={accent} />;
    }
    return <RankedBars plan={plan} result={aggregate} onDrill={onDrill} accent={accent} />;
}

function RankedBars({ plan, result, onDrill, accent }: { plan: AssistChartPlan; result: AdHocAggregateResult; onDrill: (field: AnalyticsField, value: string) => void; accent: VizAccent }): ReactElement {
    const metric = plan.metric;
    const total = result.totals[metric];
    const max = result.rows.reduce((peak, row) => Math.max(peak, row.metrics[metric]), 0);
    const tokens = accentTokens(accent);

    if (result.rows.length === 0) {
        return <EmptyState />;
    }

    return (
        <Box style={{ border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', overflow: 'hidden' }}>
            {result.rows.map((row, index) => {
                const raw = row.keys[plan.primaryField] ?? '—';
                const value = row.metrics[metric];
                const share = total === 0 ? 0 : value / total;
                const widthPct = max === 0 ? 0 : (value / max) * 100;
                const drillable = raw !== '—';
                return (
                    <Box
                        key={`${raw}-${index}`}
                        onClick={drillable ? (): void => onDrill(plan.primaryField, raw) : undefined}
                        className={drillable ? 'topic-drill-row' : undefined}
                        style={{
                            padding: '10px 14px',
                            cursor: drillable ? 'pointer' : 'default',
                            borderBottom: index < result.rows.length - 1 ? '1px solid var(--bm-color-border-subtle)' : undefined,
                        }}
                    >
                        <Group justify="space-between" gap="sm" mb={5} wrap="nowrap">
                            <Text size="sm" fw={600} truncate style={{ minWidth: 0 }}>
                                {displayLabel(plan.primaryField, raw)}
                            </Text>
                            <Group gap={8} wrap="nowrap" style={{ flexShrink: 0 }}>
                                <Text size="sm" ff="monospace" fw={700}>
                                    {formatMetricValue(metric, value)}
                                </Text>
                                <Text size="xs" ff="monospace" c="dimmed" style={{ width: 38, textAlign: 'right' }}>
                                    {formatPercent(share)}
                                </Text>
                            </Group>
                        </Group>
                        <Box style={{ height: 7, background: 'var(--bm-color-surface)', borderRadius: 3 }}>
                            <Box style={{ height: '100%', width: `${widthPct}%`, background: tokens.solid, borderRadius: 3 }} />
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

interface DonutSlice {
    raw: string;
    label: string;
    value: number;
    share: number;
    drillable: boolean;
}

function DonutChart({ plan, result, onDrill, accent }: { plan: AssistChartPlan; result: AdHocAggregateResult; onDrill: (field: AnalyticsField, value: string) => void; accent: VizAccent }): ReactElement {
    const metric = plan.metric;
    const total = result.totals[metric];

    const slices = useMemo<DonutSlice[]>(() => {
        const all = result.rows.map((row) => {
            const raw = row.keys[plan.primaryField] ?? '—';
            const value = row.metrics[metric];
            return { raw, label: displayLabel(plan.primaryField, raw), value, share: total === 0 ? 0 : value / total, drillable: raw !== '—' };
        });
        if (all.length <= MAX_DONUT_SLICES) return all;
        const head = all.slice(0, MAX_DONUT_SLICES - 1);
        const tail = all.slice(MAX_DONUT_SLICES - 1);
        const tailValue = tail.reduce((sum, slice) => sum + slice.value, 0);
        head.push({ raw: '__other__', label: `Other (${tail.length})`, value: tailValue, share: total === 0 ? 0 : tailValue / total, drillable: false });
        return head;
    }, [result.rows, plan.primaryField, metric, total]);

    if (slices.length === 0) {
        return <EmptyState />;
    }

    const colorFor = (index: number): string => accentRgba(accent, Math.max(0.25, 1 - index * 0.1));

    return (
        <Group align="center" gap="xl" wrap="nowrap" style={{ border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', padding: 'var(--mantine-spacing-md)' }}>
            <Box style={{ width: 180, height: 180, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={slices}
                            dataKey="value"
                            nameKey="label"
                            innerRadius={52}
                            outerRadius={84}
                            paddingAngle={1}
                            stroke="var(--bm-color-body)"
                            strokeWidth={2}
                            onClick={(_, index) => {
                                const slice = slices[index];
                                if (slice !== undefined && slice.drillable) onDrill(plan.primaryField, slice.raw);
                            }}
                        >
                            {slices.map((slice, index) => (
                                <Cell key={slice.raw} fill={colorFor(index)} style={{ cursor: slice.drillable ? 'pointer' : 'default', outline: 'none' }} />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(value: number) => [formatMetricValue(metric, value), metricLabel(metric)]}
                            contentStyle={{ background: 'var(--bm-color-body)', border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', fontSize: 12 }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </Box>
            <Stack gap={5} style={{ flex: 1, minWidth: 0 }}>
                {slices.map((slice, index) => (
                    <Group
                        key={slice.raw}
                        justify="space-between"
                        gap="sm"
                        wrap="nowrap"
                        onClick={slice.drillable ? (): void => onDrill(plan.primaryField, slice.raw) : undefined}
                        className={slice.drillable ? 'topic-drill-row' : undefined}
                        style={{ padding: '4px 6px', borderRadius: 4, cursor: slice.drillable ? 'pointer' : 'default' }}
                    >
                        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                            <Box style={{ width: 10, height: 10, borderRadius: 2, background: colorFor(index), flexShrink: 0 }} />
                            <Text size="sm" fw={600} truncate style={{ minWidth: 0 }}>
                                {slice.label}
                            </Text>
                        </Group>
                        <Group gap={10} wrap="nowrap" style={{ flexShrink: 0 }}>
                            <Text size="sm" ff="monospace" fw={700}>
                                {formatCount(slice.value)}
                            </Text>
                            <Text size="xs" ff="monospace" c="dimmed" style={{ width: 38, textAlign: 'right' }}>
                                {formatPercent(slice.share)}
                            </Text>
                        </Group>
                    </Group>
                ))}
            </Stack>
        </Group>
    );
}

function TrendChart({ plan, trend, accent }: { plan: AssistChartPlan; trend: AdHocTrendResult; accent: VizAccent }): ReactElement {
    const metric = plan.metric;
    const tokens = accentTokens(accent);
    const gradientId = `assist-trend-gradient-${accent}`;
    const data = useMemo(() => trend.points.map((point) => ({ bucket: point.bucket, value: point.metrics[metric] })), [trend.points, metric]);

    if (data.length === 0) {
        return <EmptyState />;
    }

    return (
        <Box style={{ border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', padding: 'var(--mantine-spacing-md)' }}>
            <Box style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                        <defs>
                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={tokens.solid} stopOpacity={0.35} />
                                <stop offset="100%" stopColor={tokens.solid} stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--bm-color-border)" vertical={false} />
                        <XAxis dataKey="bucket" tick={{ fontSize: 11 }} stroke="var(--bm-color-text-dimmed)" minTickGap={24} />
                        <YAxis tickFormatter={(value: number) => formatCompact(value)} tick={{ fontSize: 11 }} stroke="var(--bm-color-text-dimmed)" width={52} />
                        <Tooltip
                            formatter={(value: number) => [formatMetricValue(metric, value), metricLabel(metric)]}
                            contentStyle={{ background: 'var(--bm-color-body)', border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', fontSize: 12 }}
                        />
                        <Area type="monotone" dataKey="value" name={metricLabel(metric)} stroke={tokens.solid} strokeWidth={2} fill={`url(#${gradientId})`} dot={false} />
                    </AreaChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
}

function EmptyState(): ReactElement {
    return (
        <Box style={{ border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', padding: 'var(--mantine-spacing-lg)' }}>
            <Text size="sm" c="dimmed" ta="center">
                No matching activity in the current view.
            </Text>
        </Box>
    );
}
